import { anyOf, arrayOf, enumOf, is, objectOf, recordOf } from "@altostra/type-validations";
import { boolean, string } from "@altostra/type-validations/lib/primitives";

import type { SimulationMockValue } from "./types";

const nonnegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

const isMockValue = (value: unknown): value is SimulationMockValue => {
  if (value === null || typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isMockValue);
  }
  return typeof value === "object" && Object.values(value).every(isMockValue);
};

export const isSimulationWebhookMock = objectOf({
  definition_id: string,
  fields: recordOf({ key: string, value: isMockValue }),
});

export const isSimulationCase = objectOf({
  id: string,
  name: string,
  first_message: string,
  scenario: string,
  expected_outcomes: arrayOf(string),
  user_id: string,
  workflow_ids: arrayOf(string),
  webhook_mocks: arrayOf(isSimulationWebhookMock),
  created_at: string,
  updated_at: string,
});

const isExecutionStatus = enumOf("running", "completed", "error");
const isEvaluationStatus = enumOf("pending", "passed", "failed", "inconclusive", "error");

export const isSimulationCaseExecution = objectOf({
  id: string,
  case_id: string,
  session_id: string,
  status: isExecutionStatus,
  evaluation_status: isEvaluationStatus,
  evaluation_results: arrayOf(
    objectOf({
      outcome_index: nonnegativeInteger,
      status: enumOf("passed", "failed", "inconclusive"),
      explanation: anyOf(string, is(null)),
    })
  ),
  error_code: anyOf(string, is(null)),
  case_edited_since_run: boolean,
});

const runFields = {
  id: string,
  status: isExecutionStatus,
  case_ids: arrayOf(string),
  execution_counts: recordOf({ key: isExecutionStatus, value: nonnegativeInteger }),
  evaluation_counts: recordOf({ key: isEvaluationStatus, value: nonnegativeInteger }),
  created_at: string,
  updated_at: string,
};

export const isSimulationCaseResult = objectOf({ result: isSimulationCase });
export const isSimulationCasesResult = objectOf({ hits: nonnegativeInteger, results: arrayOf(isSimulationCase) });
export const isSimulationRunResult = objectOf({ result: objectOf(runFields) });
export const isSimulationRunsResult = objectOf({ hits: nonnegativeInteger, results: arrayOf(objectOf(runFields)) });
export const isSimulationRunDetailResult = objectOf({
  result: objectOf({
    ...runFields,
    executions: arrayOf(isSimulationCaseExecution),
    cases: arrayOf(isSimulationCase),
  }),
});
export const isSimulationWebhooksResult = objectOf({
  results: arrayOf(
    objectOf({
      definition_id: string,
      name: string,
      description: string,
      tool_name: string,
      response_fields: arrayOf(objectOf({ name: string, description: string })),
    })
  ),
});
